import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useSessionStore } from '@/stores/useSessionStore'
import { useMe } from '@/features/auth/api'
import {
  getPublicAuthApexHostname,
  readPasswordResetTokenFromState,
  readPostLogoutSkipAuthApexRedirectFlag,
} from '@/features/auth/lib'
import { RedirectToPublicAuthApex } from './RequireGuest'

function hasResetToken(
  pathToken: string | undefined,
  searchToken: string | null,
  locationState: unknown,
): boolean {
  if (pathToken) return true
  if (searchToken) return true
  return readPasswordResetTokenFromState(locationState) !== null
}

/**
 * `/reset-password` serves both email-token recovery (guest) and authenticated
 * platform-admin mandatory password change (BFP-517).
 */
export function ResetPasswordGuard() {
  const onTenantAuthHost =
    globalThis.window !== undefined &&
    getPublicAuthApexHostname(globalThis.location.hostname) !==
      globalThis.location.hostname

  if (onTenantAuthHost && !readPostLogoutSkipAuthApexRedirectFlag()) {
    return <RedirectToPublicAuthApex />
  }

  return <ResetPasswordGuardBody />
}

function ResetPasswordGuardBody() {
  const location = useLocation()
  const { token: pathToken } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const searchToken = searchParams.get('token')
  const hasToken = hasResetToken(pathToken, searchToken, location.state)

  // Email recovery links carry a token — skip /me bootstrap and render immediately.
  if (hasToken) {
    return <Outlet />
  }

  return <ResetPasswordSessionGate />
}

function ResetPasswordSessionGate() {
  const { isAuthenticated, user, explicitLogout, clearSession } = useSessionStore()
  const { isLoading, isError } = useMe()

  useEffect(() => {
    if (!isError) return
    clearSession()
  }, [isError, clearSession])

  if (isError) {
    return <Navigate to="/login" replace />
  }

  if (
    !explicitLogout &&
    !isAuthenticated &&
    !user &&
    isLoading
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (user?.requiresPasswordReset) {
    return <Outlet />
  }

  if (isAuthenticated && user) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}
